import { ESessionPhase } from '@lbs/shared-rp-types'
import { CARD_ANIM_DURATION } from '../constants'
import { ERpAction, ERpEvent, RussianPokerMachineEvent } from '../types'
import { ERDecisionState, ERPlayState } from './types'

export { ERDecisionState, ERPlayState } from './types'

const isPhase =
  (phase: ESessionPhase) => (_: unknown, e: RussianPokerMachineEvent) =>
    e.type === ERpEvent.SessionPhaseReceived && e.phase === phase

// Gameplay states — used as Joined.states
//
// Syncing (initial) — reads store snapshot on join/reconnect, routes to correct play state.
//   Uses always-guards so the machine jumps directly to the right substate without
//   emitting artificial phase events from the service layer.
//
// Playing (initial: Betting after normal join)
//   Normal sequence: Betting → Dealing → Decisions → Settlement → Finished
//   Each state handles only its expected next phase.
//
//   Force restart: server may send BETTING from any phase, including BETTING itself.
//   Handled at the Playing level so all substates are covered.
//
// Decisions substates:
//   waitingActiveBox  — waiting for BoxPatchReceived for the current boxOnTurn seat.
//   activeBoxReady    — box has real cards, card-flip animation is running.
//   activeBoxRevealed — animation done (ACTIVE_BOX_REVEAL_DONE), interactive controls shown.
//
// MobX patch discipline:
//   store.patchSession / store.patchBox are called inside machine actions
//   (ApplySessionPatch / ApplyBoxPatch), not in service callbacks.
//   This ensures MobX and XState state always update atomically.
export const gamePlayStates = {
  [ERPlayState.Syncing]: {
    // Immediately resolves to the correct play state based on store snapshot.
    // No services — pure synchronous guards reading store data from JoinSuccess.
    // ResetTransientContext on every path clears stale deferred/animation fields
    // from any previous session (e.g. reconnect after dealing/decisions).
    always: [
      {
        cond: 'isSnapshotDecisionsWithReadyBox',
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Decisions}.${ERDecisionState.ActiveBoxReady}`,
        actions: [
          ERpAction.ResetTransientContext,
          ERpAction.SetBoxOnTurnFromSnapshot,
        ],
      },
      {
        cond: 'isSnapshotDecisions',
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Decisions}.${ERDecisionState.WaitingActiveBox}`,
        actions: [
          ERpAction.ResetTransientContext,
          ERpAction.SetBoxOnTurnFromSnapshot,
        ],
      },
      {
        cond: 'isSnapshotSettlement',
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Settlement}`,
        actions: [
          ERpAction.ResetTransientContext,
          ERpAction.SetBoxOnTurnFromSnapshot,
        ],
      },
      {
        cond: 'isSnapshotFinished',
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Finished}`,
        actions: ERpAction.ResetTransientContext,
      },
      {
        cond: 'isSnapshotDealing',
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Dealing}`,
        actions: [
          ERpAction.ResetTransientContext,
          ERpAction.SetBoxOnTurnFromSnapshot,
        ],
      },
      {
        // Default: BETTING or no session data yet
        target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Betting}`,
        actions: ERpAction.ResetTransientContext,
      },
    ],
  },

  [ERPlayState.Playing]: {
    initial: ERPlayState.Betting,
    on: {
      [ERpEvent.NewSession]: {
        target: `#russianPoker.joined.${ERPlayState.Syncing}`,
        actions: [
          ERpAction.SetSessionId,
          ERpAction.ApplyGamePatch,
          ERpAction.ResetTransientContext,
        ],
      },
      [ERpEvent.BoxTurnReceived]: {
        actions: [ERpAction.SetBoxOnTurn, ERpAction.ApplySessionPatch],
      },
      // Generic box patch fallback — applied immediately in betting/settlement/finished.
      // Overridden by more specific handlers in dealing and decisions substates.
      [ERpEvent.BoxPatchReceived]: {
        actions: ERpAction.ApplyBoxPatch,
      },
      // Generic session patch (no phase, no boxOnTurn) — applied immediately.
      [ERpEvent.SessionPatchReceived]: {
        actions: ERpAction.ApplySessionPatch,
      },
      // Server can force-reset to BETTING from any phase, including BETTING itself
      [ERpEvent.SessionPhaseReceived]: {
        target: `.${ERPlayState.Betting}`,
        cond: isPhase(ESessionPhase.BETTING),
        actions: [ERpAction.ApplySessionPatch, ERpAction.ResetTransientContext],
      },
    },
    states: {
      [ERPlayState.Betting]: {
        on: {
          [ERpEvent.SessionPhaseReceived]: {
            target: ERPlayState.Dealing,
            cond: isPhase(ESessionPhase.DEALING),
            actions: ERpAction.ApplySessionPatch,
          },
        },
      },

      [ERPlayState.Dealing]: {
        initial: ERPlayState.DealingIdle,
        on: {
          [ERpEvent.CardQueued]: {
            actions: ERpAction.EnqueueCard,
          },
          // Override parent playing.on.BoxTurnReceived during dealing:
          // buffer seatNumber and sessionPatch, apply atomically on decisions entry.
          [ERpEvent.BoxTurnReceived]: {
            actions: [
              ERpAction.StoreDeferredBoxOnTurn,
              ERpAction.StoreDeferredSessionPatch,
            ],
          },
          // Box patches during dealing: enqueue for animation + buffer real cards by seat.
          // Machine decides — service just normalizes the event.
          [ERpEvent.BoxPatchReceived]: {
            actions: [
              ERpAction.EnqueueBoxPatch,
              ERpAction.StoreDeferredBoxPatch,
            ],
          },
        },
        states: {
          [ERPlayState.DealingIdle]: {
            always: {
              cond: 'hasCardInQueue',
              target: ERPlayState.DealingAnimating,
            },
            on: {
              [ERpEvent.SessionPhaseReceived]: {
                target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Decisions}`,
                cond: isPhase(ESessionPhase.DECISIONS),
                // Queue is already empty in dealingIdle, but deferred box/session data
                // may still exist (out-of-order boxOnTurn/BOX near phase boundary).
                // Flush them before entering decisions.
                actions: [
                  ERpAction.ApplySessionPatch,
                  ERpAction.ApplyDeferredSessionPatches,
                  ERpAction.ClearDeferredSessionPatches,
                  ERpAction.FlushDeferredBoxOnTurn,
                  ERpAction.ClearCardQueue,
                ],
              },
            },
          },

          [ERPlayState.DealingAnimating]: {
            entry: ERpAction.ApplyNextCard,
            after: {
              [CARD_ANIM_DURATION]: [
                // Drain the full queue first — every card plays its animation.
                // hasDeferredDecisionsPhase is checked only when the queue is empty:
                // the server sends real card data as the last event before DECISIONS,
                // so processing the full queue guarantees real cards are in the store.
                {
                  cond: 'hasCardInQueue',
                  target: ERPlayState.DealingAnimating,
                },
                {
                  cond: 'hasDeferredDecisionsPhase',
                  target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Decisions}`,
                  // Apply all buffered session patches and boxOnTurn atomically
                  // with the decisions transition — store and XState update together.
                  actions: [
                    ERpAction.ApplyDeferredSessionPatches,
                    ERpAction.ClearDeferredSessionPatches,
                    ERpAction.FlushDeferredBoxOnTurn,
                    ERpAction.ClearDeferredPhaseAfterDealing,
                    ERpAction.ClearCardQueue,
                  ],
                },
                { target: ERPlayState.DealingIdle },
              ],
            },
            on: {
              [ERpEvent.SessionPhaseReceived]: {
                cond: isPhase(ESessionPhase.DECISIONS),
                // Store patch for deferred apply — do NOT patch store yet.
                // store.session.phase must stay 'dealing' until the queue drains.
                actions: [
                  ERpAction.StoreDeferredSessionPatch,
                  ERpAction.DeferDecisionsPhase,
                ],
              },
              // Dev step mode: same transitions as after(CARD_ANIM_DURATION).
              [ERpEvent.DebugStep]: [
                {
                  cond: 'hasCardInQueue',
                  target: ERPlayState.DealingAnimating,
                },
                {
                  cond: 'hasDeferredDecisionsPhase',
                  target: `#russianPoker.joined.${ERPlayState.Playing}.${ERPlayState.Decisions}`,
                  actions: [
                    ERpAction.ApplyDeferredSessionPatches,
                    ERpAction.ClearDeferredSessionPatches,
                    ERpAction.FlushDeferredBoxOnTurn,
                    ERpAction.ClearDeferredPhaseAfterDealing,
                    ERpAction.ClearCardQueue,
                  ],
                },
                { target: ERPlayState.DealingIdle },
              ],
            },
          },
        },
      },

      [ERPlayState.Decisions]: {
        initial: ERDecisionState.WaitingActiveBox,

        // Parent handlers — active for both substates unless overridden by child.
        on: {
          [ERpEvent.BoxTurnReceived]: {
            actions: [ERpAction.SetBoxOnTurn, ERpAction.ApplySessionPatch],
          },
          // Server may skip SETTLEMENT entirely (e.g. all boxes passed — no dealer reveal needed).
          [ERpEvent.SessionPhaseReceived]: [
            {
              target: ERPlayState.Settlement,
              cond: isPhase(ESessionPhase.SETTLEMENT),
              actions: ERpAction.ApplySessionPatch,
            },
            {
              target: ERPlayState.Finished,
              cond: isPhase(ESessionPhase.FINISHED),
              actions: ERpAction.ApplySessionPatch,
            },
          ],
        },

        states: {
          [ERDecisionState.WaitingActiveBox]: {
            // If deferredBoxPatchesBySeat already has a patch for the current boxOnTurn
            // (buffered during dealing or out-of-order), apply it immediately.
            always: {
              cond: 'hasDeferredPatchForCurrentBoxOnTurn',
              target: ERDecisionState.ActiveBoxReady,
              actions: ERpAction.ApplyAndClearDeferredBoxPatch,
            },
            on: {
              [ERpEvent.BoxPatchReceived]: [
                {
                  cond: 'isPatchForCurrentBoxOnTurn',
                  target: ERDecisionState.ActiveBoxReady,
                  actions: ERpAction.ApplyBoxPatch,
                },
                {
                  // Current seat patch without revealed cards yet (availability/pending/error updates).
                  // Apply to store but stay in waiting until revealed cards arrive.
                  cond: 'isCurrentSeatPatch',
                  actions: ERpAction.ApplyBoxPatch,
                },
                {
                  // Out-of-order: BOX arrived before boxOnTurn was received.
                  // Buffer it — the always guard will pick it up after boxOnTurn is set.
                  actions: ERpAction.StoreDeferredBoxPatch,
                },
              ],
              // Override parent: stays in waitingActiveBox, then always guard re-evaluates.
              [ERpEvent.BoxTurnReceived]: {
                actions: [ERpAction.SetBoxOnTurn, ERpAction.ApplySessionPatch],
              },
            },
          },

          [ERDecisionState.ActiveBoxReady]: {
            on: {
              [ERpEvent.ActiveBoxRevealDone]: {
                target: ERDecisionState.ActiveBoxRevealed,
              },
              [ERpEvent.BoxTurnReceived]: {
                target: ERDecisionState.WaitingActiveBox,
                actions: [ERpAction.SetBoxOnTurn, ERpAction.ApplySessionPatch],
              },
              [ERpEvent.BoxPatchReceived]: [
                {
                  // Re-send after switch operation — apply only for the active seat.
                  cond: 'isCurrentSeatPatch',
                  actions: ERpAction.ApplyBoxPatch,
                },
                {
                  // Wrong seat: absorb event to prevent bubbling to playing.on catch-all.
                  // The catch-all would incorrectly apply a patch for a non-active seat.
                },
              ],
            },
          },

          [ERDecisionState.ActiveBoxRevealed]: {
            on: {
              [ERpEvent.BoxTurnReceived]: {
                target: ERDecisionState.WaitingActiveBox,
                actions: [ERpAction.SetBoxOnTurn, ERpAction.ApplySessionPatch],
              },
              [ERpEvent.BoxPatchReceived]: [
                {
                  // Re-send after switch/sixCard operation — apply only for the active seat.
                  cond: 'isCurrentSeatPatch',
                  actions: ERpAction.ApplyBoxPatch,
                },
                {
                  // Wrong seat: absorb to prevent bubbling to playing.on catch-all.
                },
              ],
            },
          },
        },
      },

      [ERPlayState.Settlement]: {
        on: {
          [ERpEvent.SessionPhaseReceived]: {
            target: ERPlayState.Finished,
            cond: isPhase(ESessionPhase.FINISHED),
            actions: ERpAction.ApplySessionPatch,
          },
        },
      },

      [ERPlayState.Finished]: {
        // SessionPhaseReceived(BETTING) handled by parent playing.on (normal cycle + force restart)
      },
    },
  },
}
